
	import anime from 'animejs/lib/anime.es.js';
	import {
		computed,
		defineComponent,
		onMounted,
		onUnmounted,
		ref,
	} from 'vue';
	import { useRoute } from 'vue-router';
	import { useStore } from 'vuex';
	import { Actions } from '@/store/enums/StoreEnums';
	import { setCurrentPageTitle } from '@/core/helpers/breadcrumb';
	import layoutConfig from '@/core/config/DefaultLayoutConfig';

	export default defineComponent({
		name: 'auth',
		setup() {
			const store = useStore();
			const route = useRoute();
			const logoPath = layoutConfig.main.logo.light;
			const backgroundImage = 'media/bg/bg-3.jpg';

			const heroBadge = ref<HTMLElement | null>(null);
			const heroTitle = ref<HTMLElement | null>(null);
			const heroSubtitle = ref<HTMLElement | null>(null);
			const formWrapper = ref<HTMLElement | null>(null);
			const trainEl = ref<HTMLElement | null>(null);
			const floatIcon1 = ref<HTMLElement | null>(null);
			const floatIcon2 = ref<HTMLElement | null>(null);
			const floatIcon3 = ref<HTMLElement | null>(null);
			const featureRefs = ref<(HTMLElement | null)[]>([]);

			const features = [
				{
					icon: 'bi bi-box-seam',
					title: 'Quản lý kho vật tư',
					desc: 'Theo dõi nhập – xuất – tồn kho phụ tùng',
				},
				{
					icon: 'bi bi-wrench-adjustable',
					title: 'Sửa chữa toa xe',
					desc: 'Lập kế hoạch bảo dưỡng & sửa chữa định kỳ',
				},
				{
					icon: 'bi bi-clipboard-data',
					title: 'Báo cáo & thống kê',
					desc: 'Tổng hợp dữ liệu tồn kho theo thời gian thực',
				},
			];

			const setFeatureRef = (el: unknown, index: number) => {
				if (el instanceof HTMLElement) {
					featureRefs.value[index] = el;
				}
			};

			const loginStateClass = computed(() => {
				switch (route.name) {
					case 'sign-up':
						return 'login-signup-on';
					case 'password-reset':
						return 'login-forgot-on';
					default:
						return 'login-signin-on';
				}
			});

			let trainAnimation: anime.AnimeInstance | null = null;
			let floatAnimations: anime.AnimeInstance[] = [];

			const runEntranceAnimations = () => {
				const tl = anime.timeline({ easing: 'easeOutExpo' });

				if (heroBadge.value) {
					tl.add({
						targets: heroBadge.value,
						opacity: [0, 1],
						translateY: [-16, 0],
						duration: 600,
					});
				}

				if (heroTitle.value) {
					tl.add(
						{
							targets: heroTitle.value,
							opacity: [0, 1],
							translateX: [-40, 0],
							duration: 800,
						},
						'-=300',
					);
				}

				if (heroSubtitle.value) {
					tl.add(
						{
							targets: heroSubtitle.value,
							opacity: [0, 1],
							translateX: [-30, 0],
							duration: 700,
						},
						'-=500',
					);
				}

				const validFeatures = featureRefs.value.filter(Boolean);
				if (validFeatures.length) {
					tl.add(
						{
							targets: validFeatures,
							opacity: [0, 1],
							translateX: [-30, 0],
							duration: 600,
							delay: anime.stagger(120),
						},
						'-=400',
					);
				}

				if (formWrapper.value) {
					tl.add(
						{
							targets: formWrapper.value,
							opacity: [0, 1],
							translateY: [24, 0],
							duration: 800,
						},
						'-=600',
					);
				}

				const floatIcons = [
					floatIcon1.value,
					floatIcon2.value,
					floatIcon3.value,
				].filter(Boolean) as HTMLElement[];

				floatIcons.forEach((icon, i) => {
					anime({
						targets: icon,
						opacity: [0, 0.7],
						scale: [0.5, 1],
						duration: 800,
						delay: 800 + i * 200,
						easing: 'easeOutElastic(1, .6)',
					});

					floatAnimations.push(
						anime({
							targets: icon,
							translateY: [-8, 8],
							duration: 3000 + i * 500,
							direction: 'alternate',
							loop: true,
							easing: 'easeInOutSine',
							delay: 1500 + i * 300,
						}),
					);
				});
			};

			const runTrainLoop = () => {
				if (!trainEl.value || !heroPanel.value) return;

				const panelWidth = heroPanel.value.offsetWidth;
				trainAnimation = anime({
					targets: trainEl.value,
					translateX: [-220, panelWidth + 50],
					duration: 12000,
					easing: 'linear',
					loop: true,
				});
			};

			const heroPanel = ref<HTMLElement | null>(null);

			onMounted(() => {
				setCurrentPageTitle('Đăng nhập');
				store.dispatch(Actions.ADD_BODY_CLASSNAME, 'bg-body');

				requestAnimationFrame(() => {
					runEntranceAnimations();
					runTrainLoop();
				});
			});

			onUnmounted(() => {
				store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'bg-body');
				trainAnimation?.pause();
				floatAnimations.forEach((a) => a.pause());
			});

			return {
				logoPath,
				backgroundImage,
				loginStateClass,
				features,
				setFeatureRef,
				heroBadge,
				heroTitle,
				heroSubtitle,
				formWrapper,
				trainEl,
				heroPanel,
				floatIcon1,
				floatIcon2,
				floatIcon3,
			};
		},
	});
